import {
  Heading as ChakraHeading,
  HeadingProps as ChakraHeadingProps,
  Text as ChakraText,
  TextProps as ChakraTextProps,
  useMultiStyleConfig,
  useStyleConfig,
} from '@chakra-ui/react';
import React from 'react';

type ExposedHeadingProps = ChakraHeadingProps;

const H1V2: React.FC<ExposedHeadingProps> = ({
  as,
  size = '3xl',
  children,
  colorScheme,

  ...props
}) => {
  const styles = useMultiStyleConfig('HeadingV2', { colorScheme, size });
  return (
    <ChakraHeading sx={styles} as={as || 'h1'} {...props}>
      {children}
    </ChakraHeading>
  );
};

const H2V2: React.FC<ExposedHeadingProps> = ({
  as,
  size = '2xl',
  children,
  colorScheme,
  ...props
}) => {
  const styles = useMultiStyleConfig('HeadingV2', { colorScheme, size });
  return (
    <ChakraHeading sx={styles} as={as || 'h2'} {...props}>
      {children}
    </ChakraHeading>
  );
};

const H3V2: React.FC<ExposedHeadingProps> = ({
  as,
  size = 'xl',
  children,
  colorScheme,
  ...props
}) => {
  const styles = useMultiStyleConfig('HeadingV2', { colorScheme, size });
  return (
    <ChakraHeading sx={styles} as={as || 'h3'} {...props}>
      {children}
    </ChakraHeading>
  );
};

const H4V2: React.FC<ExposedHeadingProps> = ({
  as,
  size = 'lg',
  children,
  colorScheme,
  ...props
}) => {
  const styles = useMultiStyleConfig('HeadingV2', { colorScheme, size });
  return (
    <ChakraHeading sx={styles} as={as || 'h4'} {...props}>
      {children}
    </ChakraHeading>
  );
};

const H5V2: React.FC<ExposedHeadingProps> = ({
  as,
  size = 'md',
  children,
  colorScheme,
  ...props
}) => {
  const styles = useMultiStyleConfig('HeadingV2', { colorScheme, size });
  return (
    <ChakraHeading sx={styles} as={as || 'h5'} {...props}>
      {children}
    </ChakraHeading>
  );
};

const H6V2: React.FC<ExposedHeadingProps> = ({
  as,
  size = 'sm',
  children,
  colorScheme,
  ...props
}) => {
  const styles = useMultiStyleConfig('HeadingV2', { colorScheme, size });
  return (
    <ChakraHeading sx={styles} as={as || 'h6'} {...props}>
      {children}
    </ChakraHeading>
  );
};

const Subtitle: React.FC<ChakraHeadingProps> = ({
  as,
  children,
  colorScheme,
  size = 'xs',
  ...props
}) => {
  const styles = useMultiStyleConfig('HeadingV2', { colorScheme, size });
  return (
    <ChakraHeading sx={styles} as={as || 'h6'} {...props}>
      {children}
    </ChakraHeading>
  );
};

const HeadingMap = {
  h1: H1V2,
  h2: H2V2,
  h3: H3V2,
  h4: H4V2,
  h5: H5V2,
  h6: H6V2,
};

type HeadingLevel = keyof typeof HeadingMap;

interface HeadingRendererProps {
  children: React.ReactNode;
  colorScheme?: ExposedHeadingProps['colorScheme'];
  level?: HeadingLevel;
  as?: HeadingLevel;
  align?: ExposedHeadingProps['textAlign'];
  variant?: 'overline';
  width?: ExposedHeadingProps['width'];
}

const Heading: React.FC<HeadingRendererProps> = ({
  level = 'h2',
  as = 'h2',
  variant,
  children,
  align,
  colorScheme,
  ...props
}) => {
  if (!children || !level) {
    return null;
  }

  const HeadingElement = HeadingMap[level];

  if (variant === 'overline') {
    return (
      <Overline as={level} colorScheme={colorScheme} {...props}>
        {children}
      </Overline>
    );
  }

  if (variant === 'subtitle_small') {
    return (
      <Subtitle as={level} size="xxs" colorScheme={colorScheme} {...props}>
        {children}
      </Subtitle>
    );
  }

  if (variant === 'subtitle_large') {
    return (
      <Subtitle as={level} size="xs" colorScheme={colorScheme} {...props}>
        {children}
      </Subtitle>
    );
  }

  return (
    <HeadingElement
      as={as}
      textAlign={align}
      variant={variant}
      colorScheme={colorScheme}
      {...props}
    >
      {children}
    </HeadingElement>
  );
};

const BlockquoteV2: React.FC<ChakraTextProps> = ({ children, ...props }) => {
  const style = useStyleConfig('Blockquote');
  return (
    <ChakraText sx={style} {...props} as="blockquote">
      {children}
    </ChakraText>
  );
};

const Overline: React.FC<ExposedHeadingProps> = ({
  children,
  colorScheme,
  as = 'h2',
  ...props
}) => {
  const style = useMultiStyleConfig('HeadingV2', {
    variant: 'overline',
    colorScheme,
  });

  return (
    <ChakraText
      as={as || 'h2'}
      sx={style}
      style={{
        fontSize: '14px',
        textTransform: 'uppercase',
        wordSpacing: '0.125em',
        mixBlendMode: colorScheme === `lightblue` ? 'multiply' : undefined,
        color:
          colorScheme === 'lightblue'
            ? `#00549F`
            : colorScheme === 'darkblue'
            ? '#B5D5F4'
            : '#747474',
      }}
      {...props}
    >
      {children}
    </ChakraText>
  );
};

const Caption: React.FC<ChakraTextProps> = ({ children, ...props }) => {
  return (
    <ChakraText fontSize="12px" color="wl-blue.navy" {...props} as="caption">
      {children}
    </ChakraText>
  );
};

const Disclosure: React.FC<ChakraTextProps> = ({ children, ...props }) => {
  return (
    <ChakraText fontSize="12px" {...props}>
      {children}
    </ChakraText>
  );
};

const DecorativeText: React.FC<ChakraTextProps> = ({
  children,
  colorScheme,
  size,
  textAlign,
  width,
  ...props
}) => {
  const styles = useStyleConfig('DecorativeText', {
    variant: size === 'sm' ? 'secondary' : 'primary',
    size,
    colorScheme,
  });

  return (
    <ChakraText sx={styles} {...props} width={width} textAlign={textAlign}>
      {children}
    </ChakraText>
  );
};

const ImageCaptionV2: React.FC<ChakraTextProps> = ({ children, ...props }) => {
  return (
    <ChakraText color="gray.600" fontSize="xs" fontStyle="italic" {...props}>
      {children}
    </ChakraText>
  );
};

const TextV2: React.FC<ChakraTextProps> = ({
  children,
  as,
  variant,
  size,
  colorScheme,
  ...props
}) => {
  if (variant === 'blockquote') {
    return <BlockquoteV2 {...props}>{children}</BlockquoteV2>;
  }
  if (variant === 'caption') {
    return <Caption {...props}>{children}</Caption>;
  }

  if (variant === 'disclosure') {
    return <Disclosure color={colorScheme || '#003366'}>{children}</Disclosure>;
  }
  if (variant === 'decorative') {
    return (
      <DecorativeText size={size} colorScheme={colorScheme} {...props}>
        {children}
      </DecorativeText>
    );
  }

  return (
    <ChakraText
      as={as}
      {...props}
      lineHeight={{ base: '22px', lg: '24px' }}
      fontSize={{ base: '15px', lg: '16px' }}
    >
      {children}
    </ChakraText>
  );
};

export {
  Caption,
  Disclosure,
  H1V2,
  H2V2,
  H3V2,
  H4V2,
  H5V2,
  H6V2,
  Heading,
  ImageCaptionV2,
  Overline,
  Subtitle,
  TextV2,
};
